import { expandGraph } from '../compiler/build-graph.js';
import { levenshtein } from './autocomplete.js';
/**
 * Lexical (BM25) stage with the description title-boost.
 *
 * Each doc's normalised BM25 score is raised when its DESCRIPTION (title)
 * contains the query verbatim (`fullHit`, +1.0) or a curated synonym token
 * (`tokenHit`, +0.5). The boost is what lets the canonical occupation outrank
 * docs that only mention the term via baked body/synonym text (BM25 is flat
 * across fields). `applyTitleBoost` is false on the lighter `lexicalQuery()`
 * path, which returns raw normalised BM25 without the boost.
 */
export function lexicalStage(applyTitleBoost) {
    return {
        stage: 'lexical',
        retrieve(ctx, view) {
            const out = new Map();
            const boostTokens = ctx.boostTokens;
            const fullQuery = ctx.fullQuery;
            for (const r of ctx.lexicalResults) {
                const desc = (view.docByCode.get(r.code)?.description ?? '').toLowerCase();
                const fullHit = desc.includes(fullQuery);
                const tokenHit = boostTokens.some((t) => desc.includes(t));
                const boost = applyTitleBoost ? (fullHit ? 1.0 : tokenHit ? 0.5 : 0) : 0;
                const base = ctx.maxLex > 0 ? r.score / ctx.maxLex : 0;
                out.set(r.code, base + boost);
            }
            return out;
        },
    };
}
/**
 * Edit-distance stage: for query tokens absent from the inverted index, find
 * the nearest indexed term(s) by edit distance and attribute their postings to
 * the `fuzzy` stage with a confidence derived from the distance. The nearest
 * terms are retrieved from the precomputed BK-tree (see bk-tree.ts) — exact
 * edit-distance search over the whole vocabulary without a linear scan — so
 * first-letter typos like "Elctrician" still resolve to "electrician".
 */
export function fuzzyTokens(text, view) {
    const out = new Map();
    const tokens = text
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .split(/\s+/)
        .filter((t) => t.length >= 3);
    for (const token of tokens) {
        if (view.invertedIndex.terms[token])
            continue; // lexical already covers it
        const maxDist = token.length <= 4 ? 1 : 2;
        // Pick the best near-term(s). Tie-break by term frequency so the common,
        // canonical trade word (e.g. "plumber") wins over rare dist-1 noise, and
        // the intended occupation surfaces first instead of being buried.
        const nearTerms = view.fuzzyIndex !== undefined
            ? view.fuzzyIndex.query(token, maxDist)
            : linearNearTerms(view, token, maxDist).map((term) => ({ term, dist: levenshtein(term, token) }));
        const ranked = [];
        for (const { term, dist } of nearTerms) {
            const conf = Math.max(0, 1 - dist / Math.max(token.length, term.length));
            if (conf <= 0)
                continue;
            ranked.push({ term, conf, freq: view.invertedIndex.terms[term].length });
        }
        ranked.sort((a, b) => b.conf - a.conf || b.freq - a.freq);
        for (const { term, conf } of ranked.slice(0, 2)) {
            const titleBoost = Math.min(1, conf + 0.12); // title match ranks above body mentions
            for (const docId of view.invertedIndex.terms[term]) {
                const doc = view.documents[docId];
                if (!doc)
                    continue;
                const inTitle = doc.description.toLowerCase().includes(term);
                out.set(doc.code, Math.max(out.get(doc.code) ?? 0, inTitle ? titleBoost : conf));
            }
        }
    }
    return out;
}
/**
 * Linear edit-distance scan used only when a view has no precomputed BK-tree
 * (hand-built views in tests). Mirrors the historical retrieval behaviour.
 */
function linearNearTerms(view, token, maxDist) {
    const terms = Object.keys(view.invertedIndex.terms);
    const out = [];
    for (const term of terms) {
        if (Math.abs(term.length - token.length) > maxDist)
            continue;
        if (levenshtein(term, token) <= maxDist)
            out.push(term);
    }
    return out;
}
export const fuzzyStage = {
    stage: 'fuzzy',
    retrieve(ctx, view) {
        return fuzzyTokens(ctx.raw, view);
    },
};
/**
 * Prefix stage: for each query token, surface occupations whose title, alias, or
 * task token *starts with* the token. This gives the main `query()` path the
 * partial-match recall that the trie already provides to the autocomplete
 * dropdown — typing "electr" retrieves "Electrician" and "Electrical Engineer"
 * even though neither is an exact inverted-index token. Without it, context
 * awareness could never prioritise a previously-viewed "Electrician" over a
 * near-match, because "Electrician" would be absent from the candidate pool.
 *
 * Title prefixes score highest (canonical occupation), then aliases, then tasks.
 * Uses the precomputed `prefixIndex` (word→docs, sorted) with a binary-search
 * range scan, so the per-query cost is O(log W + matches) instead of re-scanning
 * and re-lowercasing every document — keeps the hybrid path inside the latency
 * gate (search-speed.spec.ts).
 *
 * @see ../docs/search-pipeline.md — §3.7 (prefix stage).
 */
const PREFIX_TIER_SCORE = { 3: 0.85, 2: 0.65, 1: 0.45 };
export const prefixStage = {
    stage: 'prefix',
    retrieve(ctx, view) {
        const out = new Map();
        const idx = view.prefixIndex;
        if (!idx)
            return out;
        const tokens = ctx.raw
            .toLowerCase()
            .replace(/[^a-z0-9\s]/g, ' ')
            .split(/\s+/)
            .filter((t) => t.length >= 2);
        if (tokens.length === 0)
            return out;
        const { words, wordInfo } = idx;
        // Lower bound for the first word ≥ token (binary search on the sorted list).
        const lowerBound = (tok) => {
            let lo = 0;
            let hi = words.length;
            while (lo < hi) {
                const mid = (lo + hi) >>> 1;
                if (words[mid] < tok)
                    lo = mid + 1;
                else
                    hi = mid;
            }
            return lo;
        };
        for (const tok of tokens) {
            for (let i = lowerBound(tok); i < words.length; i++) {
                const w = words[i];
                if (!w.startsWith(tok))
                    break;
                const info = wordInfo.get(w);
                if (!info)
                    continue;
                const score = PREFIX_TIER_SCORE[info.tier] ?? 0.45;
                for (const code of info.codes) {
                    const prev = out.get(code);
                    if (prev === undefined || score > prev)
                        out.set(code, score);
                }
            }
        }
        return out;
    },
};
/**
 * Graph (related-occupation) stage: expand the top lexical seeds through the
 * occupation knowledge graph and score neighbouring occupations.
 */
export const graphStage = {
    stage: 'graph',
    retrieve(ctx, view) {
        const out = new Map();
        if (!view.graph)
            return out;
        const seedIds = ctx.lexicalResults.slice(0, 10).map((r) => `occ:${r.code}`);
        const expandedGraph = expandGraph(seedIds, view.graph, 2);
        for (const [nodeId, score] of expandedGraph) {
            if (nodeId.startsWith('occ:'))
                out.set(nodeId.substring(4), score);
        }
        return out;
    },
};
/**
 * Vector (semantic) stage. In the lexical-only `query()`/`lexicalQuery()` paths
 * this returns nothing — semantic search is a separate entry point
 * (`semanticQuery`) that needs query embeddings. The stage exists so a future
 * hybrid path can include vector scores through the same pipeline.
 */
export const vectorStage = {
    stage: 'vector',
    retrieve() {
        return new Map();
    },
};
//# sourceMappingURL=stages.js.map